'use client'

import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'

import { ArrowBtn } from '@/app/components/atoms/ArrowBtn'
import { Checkbox } from '@/app/components/atoms/forms/Checkbox'
import { IconCalendar } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'

import { Calendar } from '@/app/components/shared/date/Calendar'
import { DayStrip } from '@/app/components/shared/date/DayStrip'
import { isDateInRange, pickDate, type DateRange } from '@/app/components/shared/date/Calendar/utils/dateRange'
import { ARROW_SIZE, DEFAULT_DIALOG_LABEL, DEFAULT_RANGE_TOGGLE_LABEL, DEFAULT_TRIGGER_LABEL, FOCUSABLE_SELECTOR } from './DatePicker.constants'
import styles from './DatePicker.module.scss'
import type { DatePickerProps } from './DatePicker.types'

/** Día ± `amount` (comparación/aritmética por día de calendario local, no por
 * instante) — mismo criterio que `stripTime`/`isDateInRange` de este mismo
 * módulo de utilidades. */
function addDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

/**
 * `DatePicker` — selector de fecha compuesto: una tira horizontal de próximos
 * días (`DayStrip`, acceso rápido) junto a un botón que abre `Calendar` (rango
 * completo `[min, max]`) como un popover anclado — no un modal a pantalla
 * completa. Admite tanto una única fecha como un rango de dos, al estilo
 * reserva de vuelos (fiel al diseño de referencia, `eci-firefly-uikit`):
 *
 * - El PRIMER click real del visitante (tira, calendario o flecha, lo que
 *   toque antes) siempre fija una única fecha, sin más — nunca cierra un
 *   rango contra el `range` que ya traía el componente al montar. Es
 *   deliberado: todo consumidor arranca ya con una única fecha activa (p. ej.
 *   "hoy", el valor por defecto sin selección explícita del visitante,
 *   FR-007) — si ese valor de partida contara como el primer extremo de
 *   `pickDate`, el primer click en cualquier día DISTINTO de "hoy" cerraría
 *   sin querer un rango hoy→ese día, en vez de mostrar solo ese día (el caso
 *   más común: navegar por un único día concreto). `hasPickedRef` distingue
 *   "el visitante aún no ha tocado nada" (se sustituye sin más) de "ya eligió
 *   una fecha él mismo" (a partir de ahí `pickDate` decide con normalidad) —
 *   se marca en el primer click de cualquier tipo y no se reinicia mientras
 *   este `DatePicker` permanezca montado.
 * - A partir de ahí, con una única fecha activa (`range.end === null`), el
 *   siguiente click en cualquier día (tira o calendario) cierra el rango con
 *   esa fecha como el otro extremo — reordenando si hace falta (`pickDate`).
 *   Con el rango ya completo, el siguiente click reinicia a una fecha única
 *   nueva.
 * - Las flechas de avance/retroceso (±1 día) solo tienen sentido con una
 *   única fecha activa — con un rango completo no hay "un día" que desplazar,
 *   así que desaparecen (no solo se deshabilitan) mientras dure el rango.
 * - El popover del calendario completo se cierra solo automáticamente cuando
 *   el click que se acaba de hacer completa el rango (segundo click real);
 *   con una única fecha elegida se deja abierto para que el visitante pueda
 *   elegir el fin, y es él quien decide cuándo cerrarlo (clic fuera /
 *   `Escape`) — mismo criterio que `AgendaDatePicker` del diseño de
 *   referencia.
 *
 * Componente compartido y genérico (Atomic Design `shared/` — Principio III):
 * no conoce ninguna página ni mecanismo de persistencia (URL, estado
 * local...) — eso lo decide cada página consumidora vía `onChange`, igual que
 * `Calendar`/`DayStrip`.
 *
 * `hasSelection` (por defecto `true`) distingue "`range` es una elección real
 * que hay que resaltar" de "`range` es solo el ancla desde la que posicionar
 * la tira/calendario y calcular las flechas, pero nada está realmente
 * elegido" — con `false`, ningún día se pinta como seleccionado aunque
 * coincida con `range.start`/`range.end` (ver `AgendaCalendar`, que lo usa
 * mientras un filtro de fecha ajeno al calendario, p. ej. un chip de rango
 * rápido, está aplicado).
 */
export function DatePicker({
  range,
  min,
  max,
  onChange,
  onOpenChange,
  hasSelection = true,
  scrollToStart = false,
  rangeToggle = false,
  rangeToggleLabel = DEFAULT_RANGE_TOGGLE_LABEL,
  onClear,
  stripDays,
  triggerLabel = DEFAULT_TRIGGER_LABEL,
  dialogLabel = DEFAULT_DIALOG_LABEL,
  variant = 'default',
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  // Con `rangeToggle`, el modo rango solo está activo con el checkbox marcado
  // (arranca marcado si ya hay un rango cerrado); sin él, siempre se admite
  // rango (comportamiento original).
  const [rangeMode, setRangeMode] = useState(range.end !== null)
  const isRangeEnabled = !rangeToggle || rangeMode

  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // Ver el JSDoc del componente — `false` hasta el primer click real de
  // cualquier tipo (tira, calendario o flecha); a partir de ahí, siempre
  // `true` mientras este `DatePicker` esté montado.
  const hasPickedRef = useRef(false)
  // El callback se guarda en un ref (actualizado en un efecto, nunca durante el
  // render) para que `changeOpen` sea estable y los efectos de abajo no
  // reinstalen sus listeners cada vez que el consumidor lo redefina.
  const onOpenChangeRef = useRef(onOpenChange)

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange
  })

  /** Único punto por el que pasa cualquier cambio de apertura del popover, para
   * que `onOpenChange` reciba TODOS los cierres (clic fuera, `Escape` y
   * elección de día incluidos), no solo el del disparador. */
  const changeOpen = useCallback((next: boolean) => {
    setOpen(next)
    onOpenChangeRef.current?.(next)
  }, [])

  /** Resuelve un click sobre un día concreto (tira o calendario): el primer
   * click real bypasa `pickDate` y sustituye sin más (ver JSDoc del
   * componente); a partir de ahí, la reducción de dos clicks normal. */
  function resolveDayClick(clicked: Date): DateRange {
    if (!isRangeEnabled || !hasPickedRef.current) return { start: clicked, end: null }
    return pickDate(range, clicked)
  }

  /** Único punto de salida de cualquier cambio de selección (click de día o
   * flecha) — marca `hasPickedRef` antes de notificar, para que el PRÓXIMO
   * click ya compare contra una elección real del visitante. */
  function commit(next: DateRange) {
    hasPickedRef.current = true
    onChange(next)
  }

  function handleDaySelect(date: Date) {
    commit(resolveDayClick(date))
  }

  function handleCalendarSelect(date: Date) {
    const next = resolveDayClick(date)
    commit(next)
    // `next.end !== null` solo es posible si este click ACABA de cerrar un
    // rango (la única rama de `pickDate`/`resolveDayClick` que produce un
    // `end` no nulo) — nunca en el primer click real ni al reiniciar un rango
    // ya completo. Con una única fecha elegida se deja abierto para que el
    // visitante pueda elegir el fin a continuación.
    // Sin modo rango, la elección de una fecha única ya es definitiva: también cierra.
    if (next.end !== null || !isRangeEnabled) changeOpen(false)
  }

  /** Marcar/desmarcar el modo rango. Al desmarcarlo se limpia la selección
   * vigente (fecha única o rango) vía `onClear`. */
  function handleRangeModeChange(checked: boolean) {
    setRangeMode(checked)
    if (!checked) {
      hasPickedRef.current = false
      onClear?.()
    }
  }

  // Flechas de avance/retroceso (specs/042-modulo-agenda-cms, Historia 3):
  // solo existen con una única fecha activa (`range.end === null`) — con un
  // rango ya completo no hay "un día" que desplazar (FR-009/FR-010, ver
  // también el JSDoc del componente). Reinician siempre a una fecha única
  // nueva, sin pasar por `pickDate`: son "ir a este día concreto", no un click
  // de selección de rango.
  const isRangeComplete = range.end !== null
  const previousDay = isRangeComplete ? null : addDays(range.start, -1)
  const nextDay = isRangeComplete ? null : addDays(range.start, 1)
  const isPreviousDisabled = !previousDay || !isDateInRange(previousDay, min, max)
  const isNextDisabled = !nextDay || !isDateInRange(nextDay, min, max)

  function handlePrevious() {
    if (previousDay) commit({ start: previousDay, end: null })
  }

  function handleNext() {
    if (nextDay) commit({ start: nextDay, end: null })
  }

  // Cierre al hacer clic fuera del popover (y de su disparador).
  useEffect(() => {
    if (!open) return
    function handleClick(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) changeOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, changeOpen])

  // Escape cierra y devuelve el foco al disparador; al abrir, el foco entra en
  // el popover (primer enfocable) — mismo contrato modal que `SideSheet`, pero
  // sin scrim/portal (popover ligero anclado).
  useEffect(() => {
    if (!open) return
    const popover = popoverRef.current
    if (popover && !popover.contains(document.activeElement)) {
      popover.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus()
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      changeOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, changeOpen])

  // Focus-trap: Tab cíclico entre el primer y el último enfocable del popover.
  const handlePopoverKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const popover = popoverRef.current
    if (!popover) return
    const focusable = Array.from(popover.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }, [])

  const dark = variant === 'inverse'

  return (
    <div ref={containerRef} className={cx(styles.root, className)}>
      {!isRangeComplete && (
        <ArrowBtn
          dir="left"
          dark={dark}
          disabled={isPreviousDisabled}
          onClick={handlePrevious}
          width={ARROW_SIZE.width}
          height={ARROW_SIZE.height}
          className={styles.arrow}
        />
      )}

      <DayStrip
        range={range}
        min={min}
        max={max}
        onSelect={handleDaySelect}
        hasSelection={hasSelection}
        scrollToStart={scrollToStart}
        days={stripDays}
        variant={variant}
      />

      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => changeOpen(!open)}
        aria-label={triggerLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <IconCalendar size={20} />
      </button>

      {!isRangeComplete && (
        <ArrowBtn
          dir="right"
          dark={dark}
          disabled={isNextDisabled}
          onClick={handleNext}
          width={ARROW_SIZE.width}
          height={ARROW_SIZE.height}
          className={styles.arrow}
        />
      )}

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-modal="true"
          aria-label={dialogLabel}
          tabIndex={-1}
          className={styles.popover}
          onKeyDown={handlePopoverKeyDown}
        >
          <Calendar range={range} min={min} max={max} onSelect={handleCalendarSelect} hasSelection={hasSelection} />
          {rangeToggle && (
            <div className={styles.rangeToggle}>
              <Checkbox size="s" inverse label={rangeToggleLabel} checked={rangeMode} onChange={handleRangeModeChange} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
